/**
 * Quick Print Orders Page (spec 012)
 * Pantalla del iPad para imprimir los QR de cada mesa: todas las mesas
 * recientes por día, y dentro de cada día las pendientes primero.
 */

import { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { printTickets } from '@/services/ticketPrinterService';
import { toast } from 'sonner';
import { RefreshCw, LayoutGrid } from 'lucide-react';
import { deskService, type DeskOrder } from '@/services/deskService';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useAdminAuth } from '@/stores/adminAuthStore';
import { AdminPermissions } from '@/types/admin';
import { AdminPageShell, PageHeader, PageLoading } from '@/components/admin/shell';
import { AdminButton } from '@/components/admin/shell/AdminButton';
import { DayTables } from '@/components/admin/orders/DayTables';
import { hasPhotos } from '@/components/admin/orders/orderDisplay';
import { groupOrdersByServiceDay } from '@/lib/orderDays';
import { cn } from '@/lib/utils';

interface QRCodeCache {
  [orderId: string]: string;
}

export function QuickPrintOrdersPage() {
  const navigate = useNavigate();
  const { admin } = useAdminAuth();
  const canManageItems = admin ? AdminPermissions.canManageItems(admin.role) : false;
  const { trackAdminOrder } = useAnalytics();
  const [qrCodes, setQrCodes] = useState<QRCodeCache>({});
  const qrCodesRef = useRef(qrCodes);
  const [printingId, setPrintingId] = useState<string | null>(null);
  const [reprintId, setReprintId] = useState<string | null>(null);
  const isPrintingRef = useRef<string | null>(null);

  useEffect(() => {
    qrCodesRef.current = qrCodes;
  }, [qrCodes]);

  const {
    data: ordersData,
    isLoading,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['admin-orders-simple', 'all'],
    queryFn: () => deskService.getOrders('all'),
    refetchInterval: 30000,
  });

  const orders = useMemo(() => ordersData?.orders || [], [ordersData]);
  // El logo llega con las órdenes: el personal no puede leer la configuración del restaurante
  const restaurant = ordersData?.restaurant;
  const days = useMemo(() => groupOrdersByServiceDay(orders), [orders]);

  const syncMutation = useMutation({
    mutationFn: () => deskService.syncOrders(),
    onSuccess: () => {
      toast.loading('Sincronización iniciada...', { id: 'sync' });
    },
    onError: () => {
      toast.error('Error al iniciar sincronización');
    },
  });

  // Pre-generate QR codes when orders load
  useEffect(() => {
    const generateQRCodes = async () => {
      for (const order of orders) {
        if (qrCodesRef.current[order.id]) continue;

        try {
          const qrUrl = buildOrderUrl(order.provider_order_id);
          const qrDataUrl = await QRCode.toDataURL(qrUrl, {
            width: 400,
            margin: 1,
            color: {
              dark: '#000000',
              light: '#FFFFFF',
            },
          });

          setQrCodes((prev) => ({ ...prev, [order.id]: qrDataUrl }));
        } catch {
          // Silently skip failed QR generations
        }
      }
    };

    if (orders.length > 0) {
      generateQRCodes();
    }
  }, [orders]);

  const handlePrint = async (order: DeskOrder, quantity: number) => {
    if (isPrintingRef.current) return;
    isPrintingRef.current = order.id;

    if (!hasPhotos(order)) {
      toast.error('Esta orden no tiene items con foto. Añade fotos primero.');
      isPrintingRef.current = null;
      return;
    }

    if (!qrCodes[order.id]) {
      toast.error('El QR aún no está listo. Espera un momento.');
      isPrintingRef.current = null;
      return;
    }

    setPrintingId(order.id);

    try {
      await deskService.printOrder(order.id, quantity);

      const result = await printTickets(
        { qrCodeUrl: qrCodes[order.id], logoUrl: restaurant?.logo_url ?? undefined },
        {
          count: quantity,
          onProgress: (current, total) => {
            toast.info(`Imprimiendo ticket ${current}/${total}...`);
          }
        }
      );

      trackAdminOrder.printBrowser(order.provider_order_id);

      if (result.failed > 0) {
        toast.error(`Error al imprimir ${result.failed} ticket(s). ${result.success} impresos correctamente.`);
      } else {
        toast.success(`${quantity} QR enviado${quantity > 1 ? 's' : ''} a la impresora`);
      }
      setReprintId(null);
      refetch();
    } catch (err) {
      console.error('Print error:', err);
      toast.error('Error al imprimir. Verifica la impresora.');
    } finally {
      setPrintingId(null);
      isPrintingRef.current = null;
    }
  };

  if (isLoading && orders.length === 0) {
    return (
      <AdminPageShell>
        <div className="space-y-6">
          <PageHeader title="Mesas para generar QR" />
          <PageLoading variant="grid" rows={2} columns={5} />
        </div>
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell>
      <div className="space-y-6">
        <PageHeader
          title="Mesas para generar QR"
          subtitle={`${orders.length} ${orders.length === 1 ? 'mesa' : 'mesas'} recientes`}
        >
          <div className="flex items-center gap-2">
            {canManageItems && (
              <AdminButton
                onClick={() => navigate('/admin/orders/detail')}
                variant="ghost"
                className="min-h-11"
                title="Vista detallada"
                aria-label="Vista detallada"
              >
                <LayoutGrid className="h-4 w-4" />
              </AdminButton>
            )}
            <AdminButton
              onClick={() => {
                syncMutation.mutate();
                refetch();
              }}
              className="min-h-11"
            >
              <RefreshCw className={cn('mr-2 h-4 w-4', (syncMutation.isPending || isRefetching) && 'animate-spin')} />
              Actualizar
            </AdminButton>
          </div>
        </PageHeader>

        {days.length > 0 ? (
          <div className="space-y-10">
            {days.map((day) => (
              <section key={day.key} aria-label={day.label}>
                <div className="mb-6 border-b-2 border-gray-300 pb-3">
                  <h2 className="text-xl font-semibold text-gray-900">{day.label}</h2>
                </div>
                <DayTables
                  orders={day.orders}
                  printingId={printingId}
                  reprintId={reprintId}
                  canManageItems={canManageItems}
                  onPrint={handlePrint}
                  onSelectReprint={setReprintId}
                />
              </section>
            ))}
          </div>
        ) : (
          <div className="rounded-sm border border-gray-200 bg-white p-12 text-center">
            <p className="mb-2 text-gray-500">No hay mesas activas</p>
            <p className="text-sm text-gray-400">Las mesas aparecerán aquí cuando se cierren en el TPV</p>
          </div>
        )}
      </div>
    </AdminPageShell>
  );
}

// Helper function to build order URL
function buildOrderUrl(providerOrderId: string): string {
  const baseUrl = window.location.origin;
  return `${baseUrl}/order/${providerOrderId}`;
}
